import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit, PLATFORM_ID, SimpleChanges
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import {
  componentDestroyed,
  Tournament, TournamentStage, TournamentStageTeam, TournamentTeam,
  UntilDestroy
} from '@mtgame/core';
import { isPlatformBrowser } from '@angular/common';
import { getStagesRoute, getTeamPageRoute } from '@config/routes';
import { ToastService } from '@mtgame/ui';
import { TournamentStageWidgetService } from './tournament-stage-widget.service';
import { combineLatest } from 'rxjs';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-stage-widget',
  templateUrl: './tournament-stage-widget.component.html',
  styleUrls: ['./tournament-stage-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TournamentStageWidgetService,
  ]
})
export class TournamentStageWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  tournament: Tournament;
  @Input()
  hiddenHeader: boolean;
  loading = true;
  stageLoading = false;

  stages: TournamentStage[] = [];
  stage: TournamentStage;
  teams: TournamentTeam[];
  stageTeams: TournamentStageTeam[];
  isBrowser = isPlatformBrowser(this.platformId);

  subscriptions = [];

  get tournamentRoute(): string[] {
    return getStagesRoute(this.tournament.alias);
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tournamentsStageWidgetService: TournamentStageWidgetService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.tournament && this.tournament) {
      this.loading = true;
      await this.tournamentsStageWidgetService.initialize(this.tournament);
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }
    combineLatest([
      this.tournamentsStageWidgetService.stages$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentsStageWidgetService.teams$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentsStageWidgetService.stageTeams$.pipe(takeUntil(componentDestroyed(this))),
    ]).subscribe(([stages, teams, stageTeams]) => {
      this.stages = stages;
      this.teams = teams.sort((a, b) => b.points - a.points);
      this.stageTeams = stageTeams;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
    this.tournamentsStageWidgetService.dispose();
  }

  getTeamRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  getStageTeam(teamId: number, stageId: number): TournamentStageTeam {
    return this.stageTeams.find(t => t.tournamentTeam.id === teamId && t.tournamentStageId === stageId) || new TournamentStageTeam();
  }
}
